import { Link } from "react-router-dom";
import MouCasesSection from "./mou/MouCasesSection.jsx";
import OrganizationDocumentsSection from "./documents/OrganizationDocumentsSection.jsx";
import { DetailSkeleton } from "../components/Skeleton.jsx";
import {
  WORKER_LINE_STATUS_OPTIONS,
  statusBadgeVariant,
  formatDateTimeTh,
  describeDetailError,
  describeWriteError,
  nextActionLabel,
  isNavigableNextAction,
  fieldLabel,
  isJobDemandSide,
} from "./workerLineShared.js";

/**
 * Shared PLC / บมจ detail/create/edit drawer views (Prompt 132), reused by
 * both ./PlcsListPage.jsx and ./WorkerLineOrgChartPage.jsx (a PLC node's own
 * drawer) over the real GET/POST/PATCH .../worker-line/plcs API.
 */

export function PlcDetailView({ titleId, state, onEdit, onCreateAgent }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-wl-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;
  const agents = detail.agents ?? [];
  const nextActions = detail.nextActions ?? [];
  const parentSuperAgent = detail.parentSuperAgent ?? {};

  return (
    <div className="wx-wl-drawer">
      <span className="wx-wl-drawer-kicker">PLC / บมจ · DETAIL</span>
      <h2 id={titleId} className="wx-wl-drawer-title">
        {detail.displayName}
      </h2>
      <p className="wx-wl-drawer-sub">{detail.plcCode}</p>
      <div className="wx-wl-drawer-status">
        <span className={`wx-badge wx-badge--${statusBadgeVariant(detail.status)}`}>{detail.status}</span>
      </div>

      <section className="wx-wl-dsec">
        <h3>Super Agent ต้นสังกัด</h3>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("displayName")}</span>
          <strong>{parentSuperAgent.displayName || "-"}</strong>
        </div>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("status")}</span>
          <strong>{parentSuperAgent.status || "-"}</strong>
        </div>
        {parentSuperAgent.superAgentOrganizationId && (
          <p className="wx-wl-note-muted">
            <Link to={`/backoffice/super-agents/${parentSuperAgent.superAgentOrganizationId}`}>เปิด Super Agent นี้</Link>
          </p>
        )}
      </section>

      <section className="wx-wl-dsec">
        <h3>ข้อมูลทั่วไป / ผู้ติดต่อ</h3>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("legalName")}</span>
          <strong>{detail.legalName || "-"}</strong>
        </div>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("ownerName")}</span>
          <strong>{detail.ownerName || "-"}</strong>
        </div>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("primaryPhoneNumber")}</span>
          <strong>{detail.primaryPhoneNumber || "-"}</strong>
        </div>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("lineId")}</span>
          <strong>{detail.lineId || "-"}</strong>
        </div>
        {detail.note && (
          <div className="wx-wl-kv-row">
            <span>{fieldLabel("note")}</span>
            <strong>{detail.note}</strong>
          </div>
        )}
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("createdAtUtc")}</span>
          <strong>{formatDateTimeTh(detail.createdAtUtc) ?? "-"}</strong>
        </div>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("updatedAtUtc")}</span>
          <strong>{formatDateTimeTh(detail.updatedAtUtc) ?? "-"}</strong>
        </div>
      </section>

      <section className="wx-wl-dsec">
        <h3>สรุป Agent ({agents.length})</h3>
        {agents.length === 0 ? (
          <p className="wx-empty-list">ยังไม่มี Agent ใต้ PLC นี้</p>
        ) : (
          <div className="wx-wl-summary-list">
            {agents.map((agent) => (
              <div className="wx-wl-summary-row" key={agent.agentId}>
                <div className="wx-wl-summary-row-name">
                  <strong>{agent.displayName}</strong>
                  <span>{agent.agentCode}</span>
                </div>
                <span className={`wx-badge wx-badge--${statusBadgeVariant(agent.status)}`}>{agent.status}</span>
                <span>{agent.workerCount} แรงงาน</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="wx-wl-dsec">
        <h3>จำนวนรวม</h3>
        <div className="wx-wl-counts-grid">
          <div className="wx-wl-count-cell">
            <span>Agent</span>
            <strong>{detail.agentCount}</strong>
          </div>
          <div className="wx-wl-count-cell">
            <span>แรงงาน</span>
            <strong>{detail.workerCount}</strong>
          </div>
        </div>
      </section>

      {/* P164-R4: the company's OWN documents - its licence, หนังสือรับรอง,
          ภ.พ.20. Rendered for BOTH sides, with no side check at all, and that
          absence is the point: the owner's instruction was "บริษัทจัดหางานและ
          จัดหาแรงงานต้องแนบเอกสารได้". Every company has papers whether or not it
          files imports, and before this a บริษัทจัดหาแรงงาน had nowhere to keep
          them. Sits ABOVE the MOU section so the company's own record comes
          before the work it is doing. */}
      <OrganizationDocumentsSection organizationId={detail.plcOrganizationId} />

      {/* P164: the MOU import process, on the job side only. A worker-side
          บริษัทจัดหาแรงงาน files no imports, so the section is not rendered for
          one at all rather than rendered empty - and not rendering it is also
          what keeps a pointless request off every worker-side drawer open. The
          side is read off the record itself, never from which URL the drawer was
          opened from: this same view backs /plcs, /plcs/job-side and the org
          chart's own PLC node. */}
      {isJobDemandSide(detail.organizationSide) && (
        <MouCasesSection plcOrganizationId={detail.plcOrganizationId} plcName={detail.displayName} />
      )}

      <section className="wx-wl-dsec">
        <h3>ขั้นตอนถัดไป (Next Actions)</h3>
        <div className="wx-wl-next-actions">
          {nextActions.length === 0 && <span className="wx-wl-next-action-info">ไม่มีขั้นตอนแนะนำเพิ่มเติม</span>}
          {nextActions.map((code) =>
            isNavigableNextAction(code) ? (
              <Link key={code} className="wx-wl-row-action wx-wl-row-action--full" to={onCreateAgent(parentSuperAgent.superAgentOrganizationId, detail.plcOrganizationId)}>
                {nextActionLabel(code)}
              </Link>
            ) : (
              <span key={code} className="wx-wl-next-action-info">
                {nextActionLabel(code)}
              </span>
            )
          )}
        </div>
      </section>

      <div className="wx-wl-drawer-actions">
        <button type="button" className="wx-button wx-button--primary wx-wl-drawer-actions-full" onClick={onEdit}>
          แก้ไข PLC
        </button>
      </div>
    </div>
  );
}

const EMPTY_PLC_FORM = { superAgentOrganizationId: "", displayName: "", legalName: "", ownerName: "", primaryPhoneNumber: "", lineId: "", note: "" };

export function emptyPlcForm(prefill = {}) {
  return { ...EMPTY_PLC_FORM, ...prefill };
}

export function PlcCreateView({ titleId, form, onChange, superAgentOptions, validationError, submitState, onSubmit }) {
  const submitting = submitState.status === "submitting";

  return (
    <div className="wx-wl-drawer">
      <span className="wx-wl-drawer-kicker">CREATE PLC / บมจ</span>
      <h2 id={titleId} className="wx-wl-drawer-title">
        สร้าง PLC / บมจ
      </h2>
      <p className="wx-wl-drawer-sub">เพิ่ม PLC / บมจ ใหม่เข้าสายงาน</p>

      <div className="wx-wl-notice">สร้าง/แก้ไขได้เฉพาะ SuperAdmin เท่านั้น ระบบจะปฏิเสธคำขอจากผู้ใช้อื่นโดยอัตโนมัติ</div>

      <label className="wx-wl-field">
        <span>
          superAgentOrganizationId (Super Agent ต้นสังกัด) <b>*</b>
        </span>
        <select aria-label="superAgentOrganizationId" value={form.superAgentOrganizationId} onChange={(event) => onChange({ superAgentOrganizationId: event.target.value })}>
          <option value="">เลือก Super Agent...</option>
          {superAgentOptions.map((option) => (
            <option key={option.superAgentOrganizationId} value={option.superAgentOrganizationId}>
              {option.displayName} ({option.superAgentCode})
            </option>
          ))}
        </select>
      </label>
      <label className="wx-wl-field">
        <span>
          {fieldLabel("displayName")} <b>*</b>
        </span>
        <input type="text" aria-label="displayName" value={form.displayName} onChange={(event) => onChange({ displayName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>
          {fieldLabel("legalName")} <span className="wx-wl-optional">(ไม่บังคับ)</span>
        </span>
        <input type="text" aria-label="legalName" value={form.legalName} onChange={(event) => onChange({ legalName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("ownerName")}</span>
        <input type="text" aria-label="ผู้ดูแล" value={form.ownerName} onChange={(event) => onChange({ ownerName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("primaryPhoneNumber")}</span>
        <input type="tel" aria-label="primaryPhoneNumber" value={form.primaryPhoneNumber} onChange={(event) => onChange({ primaryPhoneNumber: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("lineId")}</span>
        <input type="text" aria-label="lineId" value={form.lineId} onChange={(event) => onChange({ lineId: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>
          {fieldLabel("note")} <span className="wx-wl-optional">(ไม่บังคับ)</span>
        </span>
        <textarea aria-label="note" value={form.note} onChange={(event) => onChange({ note: event.target.value })} />
      </label>

      {validationError && (
        <p className="wx-wl-form-message" role="alert">
          {validationError}
        </p>
      )}
      {submitState.status === "error" && (
        <p className="wx-wl-form-message" role="alert">
          {describeWriteError(submitState.error, "PLC นี้ถูกสร้างไปแล้ว กรุณาลองใหม่อีกครั้ง")}
        </p>
      )}

      <div className="wx-wl-drawer-actions">
        <button type="button" className="wx-button wx-button--primary wx-wl-drawer-actions-full" disabled={submitting} onClick={onSubmit}>
          {submitting ? "กำลังบันทึก..." : "บันทึก PLC"}
        </button>
      </div>
    </div>
  );
}

export function PlcEditView({ titleId, code, form, onChange, validationError, submitState, onSubmit, onBack }) {
  const submitting = submitState.status === "submitting";

  return (
    <div className="wx-wl-drawer">
      <span className="wx-wl-drawer-kicker">EDIT PLC / บมจ</span>
      <h2 id={titleId} className="wx-wl-drawer-title">
        แก้ไข PLC / บมจ
      </h2>
      <p className="wx-wl-drawer-sub">{code ?? "-"}</p>

      <label className="wx-wl-field">
        <span>
          {fieldLabel("displayName")} <b>*</b>
        </span>
        <input type="text" aria-label="displayName" value={form.displayName} onChange={(event) => onChange({ displayName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("legalName")}</span>
        <input type="text" aria-label="legalName" value={form.legalName} onChange={(event) => onChange({ legalName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("ownerName")}</span>
        <input type="text" aria-label="ผู้ดูแล" value={form.ownerName} onChange={(event) => onChange({ ownerName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("primaryPhoneNumber")}</span>
        <input type="tel" aria-label="primaryPhoneNumber" value={form.primaryPhoneNumber} onChange={(event) => onChange({ primaryPhoneNumber: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("lineId")}</span>
        <input type="text" aria-label="lineId" value={form.lineId} onChange={(event) => onChange({ lineId: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("status")}</span>
        <select aria-label="status" value={form.status} onChange={(event) => onChange({ status: event.target.value })}>
          {WORKER_LINE_STATUS_OPTIONS.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>
      <label className="wx-wl-field">
        <span>
          {fieldLabel("note")} <span className="wx-wl-optional">(ไม่บังคับ)</span>
        </span>
        <textarea aria-label="note" value={form.note} onChange={(event) => onChange({ note: event.target.value })} />
      </label>

      {validationError && (
        <p className="wx-wl-form-message" role="alert">
          {validationError}
        </p>
      )}
      {submitState.status === "error" && (
        <p className="wx-wl-form-message" role="alert">
          {describeWriteError(submitState.error, "PLC นี้ถูกแก้ไขไปแล้วก่อนหน้านี้ กรุณารีเฟรชแล้วลองใหม่")}
        </p>
      )}

      <div className="wx-wl-drawer-actions">
        <button type="button" className="wx-button wx-button--ghost" onClick={onBack} disabled={submitting}>
          ย้อนกลับ
        </button>
        <button type="button" className="wx-button wx-button--primary" onClick={onSubmit} disabled={submitting}>
          {submitting ? "กำลังบันทึก..." : "บันทึกการแก้ไข"}
        </button>
      </div>
    </div>
  );
}
